import { debug } from './modules/debug';
/**
 * Method 'parseKeyValuePairs' splits the substring (partOfURL) according to the separator into array of {key, value}
 * @param {String} partOfURL query or hash
 * @param {String} [separator='&'] Separator between parameters in hash
 * @return {Object[]} Array with separated key and value
 * Example:
 * parseKeyValuePairs('header&a=1&b=&c=3')
 * => [{key: 'header', value: undefined}, {key: 'a', value: '1'}, {key: 'b', value: ''}, {key: 'c', value: '3'}]
 */
const parseKeyValuePairs = (partOfURL, separator = '&') => {
    const keyValueList = partOfURL.split(separator).map((pair) => {
        const [key, value] = pair.split('=');
        return { key, value };
    });
    return keyValueList.length === 1 && keyValueList[0].key === '' ? [] : keyValueList;
};
/**
 * Method 'composeKeyValuePairs' collects a string of individual {key, value}
 * @param {Object[]} arr Array with separated key and value
 * @param {String} [separator='&'] Separator between parameters in hash
 * @return {Object[]} partOfURL (query or hash)
 * Example:
 * composeKeyValuePairs([{key: 'header', value: undefined}, {key: 'a', value: '1'}, {key: 'b', value: ''}, {key: 'c', value: '3'}])
 * => 'header&a=1&b=&c=3')
 */
const composeKeyValuePairs = (arr, separator = '&') => 
// eslint-disable-next-line no-nested-ternary
arr.map(({ key, value }) => (key === '' ? '' : value === undefined ? key : `${key}=${value}`)).join(separator);
/**
 * Method 'getUrlSpecs' returns the specification from the url
 */
const getUrlSpecs = (url, hash = false, hashSeparator) => ({
    partOfURL: hash ? url.hash.substring(1) : url.search.substring(1),
    separator: hash ? hashSeparator || '&' : '&',
    delimiter: hash ? '#' : '?',
});
/**
 * Method 'decodeValue' decodes part of url
 */
const decodeValue = (str) => decodeURI(str).replace(/\+/g, ' ');
/**
 * Method 'getParamVal' searches for and returns the parameter value(s) by name
 * @param {String} paramName - Parameter name (it can't be empty, it can't be made up of spaces only)
 * @param {String} [url = window.location.href] - URL-line
 * @param {Object} [configuration = {}] - Additional configuration of the function
 * @param {Boolean} [configuration.all] - Flag for searching for all values
 * all = false (default), the first value found is returned
 * all = true, the found values are returned in an array (one or more values)
 * @param {String} [configuration.part] - Part of url
 * part = 'query' (default)
 * part = 'hash'
 * @param {String} [configuration.hashSeparator] - Separator between parameters in hash
 * @return {String | String[] | null} returns a decoded value of the parameter (if there are several parameters, it returns the first value)
 * or returns null if the parameter is not found or if url is incorrect
 */
const getParamVal = (paramName, url = window.location.href, configuration = {}) => {
    try {
        debug({ state: 'start', message: 'getParamVal started', paramName, url, configuration });
        if (!paramName.trim()) {
            debug({
                message: 'The parameter name (paramName) is empty or consists only of spaces',
                paramName,
            });
            return null;
        }
        const urlObj = new URL(url);
        const { partOfURL, separator } = getUrlSpecs(urlObj, configuration.part === 'hash', configuration.hashSeparator);
        const encodedParamName = encodeURI(paramName);
        const pairsArr = parseKeyValuePairs(partOfURL, separator);
        if (configuration.all) {
            // return array of value or null
            const targetPairsArr = pairsArr
                .filter((pair) => pair.key === encodedParamName)
                .map((pair) => decodeValue(pair.value || ''));
            const result = targetPairsArr.length > 0 ? targetPairsArr : null;
            debug({ message: 'getParamVal finished', result });
            return result;
        }
        else {
            // return value or null
            const targetPair = pairsArr.find((pair) => pair.key === encodedParamName);
            const result = targetPair ? decodeValue(targetPair.value || '') : null;
            debug({ message: 'getParamVal finished', result });
            return result;
        }
    }
    catch (error) {
        debug({ type: 'error', message: 'Execution error', error });
        return null;
    }
};
/**
 * Method 'setParam' ADDS a parameter to url (if url doesn't have a parameter yet)
 *                or UPDATES the value in an existing parameter
 * @param {String} paramName - Parameter name (it can't be empty, it can't be made up of spaces only)
 * @param {String} paramValue - Parameter value
 * @param {String} [url = window.location.href] - URL-line
 * @param {Object} [configuration = {}] - additional configuration of the function
 * @param {String} [configuration.rewrite] -
 * rewrite is missing - the parameter will be added
 * rewrite = 'first' (default) - if there is one or more parameters with the same name, the value of the FIRST parameter will be overwritten, otherwise it will be added
 * rewrite = 'all' - if there is one or more parameters with the same name, then ALL their values will be overwritten, otherwise it will be added
 * rewrite = 'no' - only adds parameter, there is no check for the existence of the parameter
 * @param {String} [configuration.part] - Part of url
 * part = 'query' (default)
 * part = 'hash'
 * @param {String} [configuration.hashSeparator] - Separator between parameters in hash
 * @return {String | null} returns the encoded url with parametr
 * or returns null (if url is incorrect or something went wrong)
 */
const setParam = (paramName, paramValue = '', url = window.location.href, configuration = {}) => {
    try {
        debug({ message: 'setParam started', paramName, paramValue, url, configuration });
        if (!paramName.trim()) {
            debug({ message: 'The parameter name (paramName) is empty or consists only of spaces', url });
            return url;
        }
        const urlObj = new URL(url);
        const { partOfURL, separator, delimiter } = getUrlSpecs(urlObj, configuration.part === 'hash', configuration.hashSeparator);
        let pairsArr = parseKeyValuePairs(partOfURL, separator);
        let wasUpdate = false;
        if (configuration.rewrite !== 'no') {
            let canUpdate = true;
            pairsArr = pairsArr.map((pair) => {
                if (pair.key === paramName && canUpdate) {
                    pair.value = paramValue;
                    canUpdate = configuration.rewrite === 'all';
                    wasUpdate = true;
                }
                return pair;
            });
        }
        if (configuration.rewrite === 'no' || !wasUpdate) {
            pairsArr.push({ key: paramName, value: paramValue });
        }
        const targetPartOfURL = delimiter + composeKeyValuePairs(pairsArr, separator);
        if (configuration.part === 'hash') {
            urlObj.hash = targetPartOfURL;
        }
        else {
            urlObj.search = targetPartOfURL;
        }
        const result = urlObj.toString();
        debug({ message: 'setParam finished', result });
        return result;
    }
    catch (error) {
        debug({ type: 'error', message: 'Execution error', error });
        return null;
    }
};
/**
 * Method 'getDomainLevel' gets domain part by desired level
 * @param {String} [hostname=window.location.hostname] URL
 * @param {Number} [level=2] Domain level
 * @param {Boolean} [leadingDot=true] The need to add a leading dot at the beginning
 * @return {String | null} Domain part
 */
const getDomainLevel = (hostname = window.location.hostname, level = 2, leadingDot = true) => {
    if (level <= 0) {
        return level === 0 ? '.' : null;
    }
    hostname = hostname.replace(/https?:\/\//, ''); // https://site.com -> site.com
    hostname = hostname.split(/[\?#:\/]/)[0]; //site.com/page?a=1#head -> site.com
    let availableLevels = 0;
    const separators = hostname.match(/\./g);
    if (separators) {
        availableLevels = separators.length + 1;
    }
    if (!availableLevels || level > availableLevels) {
        return hostname;
    }
    else {
        return (leadingDot && level < availableLevels ? '.' : '') + hostname.split('.').slice(-level).join('.');
    }
};
/**
 * Removes UTM parametr (whole key=value pair) from URL.
 * @param {*} sourceURL - URL to remove UTM from.
 * @param {*} paramName - Name (key) of param to remove.
 * @return {String} URL without specific UTM.
 */
const removeParam = (sourceURL, paramName) => {
    const regex = new RegExp(`[?#&](${paramName}=[^&#]*&?)`);
    const matches = regex.exec(sourceURL);
    if (!matches || matches.length < 2) {
        return sourceURL;
    }
    const stringToReplace = matches[1];
    const newUrl = sourceURL
        // remove sznclid
        .replace(stringToReplace, '')
        // remove extra & at the end
        .replace(/&$/, '')
        // remove empty UTM followed by hash
        .replace(/\?#/, '#')
        // remove empty UTM or hash at the end of the URL
        .replace(/[?#]$/, '');
    return newUrl;
};
export { setParam, getParamVal, getDomainLevel, removeParam };
